import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';

import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { useAxios } from '@/hooks/useAxios';
import { useBulkAction } from '@/hooks/useBulkAction';
import { useMy } from '@/hooks/useMy';
import {
  assetColumns,
  assetDefaultSort,
} from '@/sections/attacks/attackPaths/components/wizards/columns/assetColumns';
import { SelectionTable } from '@/sections/attacks/attackPaths/components/wizards/components/SelectionTable';
import { WizardSearchInput } from '@/sections/attacks/attackPaths/components/wizards/components/WizardSearchInput';
import type { SearchableListItem } from '@/sections/attacks/attackPaths/components/wizards/hooks/useSearchableList';
import { useSearchableList } from '@/sections/attacks/attackPaths/components/wizards/hooks/useSearchableList';
import type { ScanProfileFooterState } from '@/sections/settings/components/primitives/scanProfileFooterState';
import type { Asset, NodeLabel, Operator } from '@/types';
import type { ScanProfile } from '@/types/scanProfile';
import { runChunked } from '@/utils/runChunked';

const MAX_SELECTION = 100;
const APPLY_CONCURRENCY = 8; // passed to runChunked concurrency option

export interface AssignProfileToAssetsModalProps {
  open: boolean;
  onClose: () => void;
  profile: ScanProfile;
  /**
   * mode='create' (default): blank selection, Skip button visible, intended for
   * the post-create chaining flow. Does NOT break the existing create path.
   *
   * mode='manage': pre-selects currently-assigned assets, no Skip button,
   * Save fires both adds (PUT profile=id) and removes (PUT profile='').
   */
  mode?: 'create' | 'manage';
  /** create mode only — called when user clicks "Skip — assign later" */
  onSkip?: () => void;
}

export interface AssignProfileToAssetsContentProps {
  /** needed for the seeding useEffect */
  open: boolean;
  onClose: () => void;
  profile: ScanProfile;
  mode?: 'create' | 'manage';
  /** create mode only — called when user clicks "Skip — assign later" */
  onSkip?: () => void;
  /** wizard: renders a Back button in left slot of footer */
  onBack?: () => void;
  /** reports footer button state up to parent modal */
  onFooterStateChange?: (state: ScanProfileFooterState) => void;
}

export function AssignProfileToAssetsContent({
  open,
  onClose,
  onSkip,
  onBack,
  profile,
  mode = 'create',
  onFooterStateChange,
}: AssignProfileToAssetsContentProps) {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isApplying, setIsApplying] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const axios = useAxios();
  const queryClient = useQueryClient();
  const submitRemoveProfileBulk = useBulkAction({
    resource: 'asset',
    action: 'remove_profile',
  });

  const [debouncedSearch] = useDebounce(
    searchTerm,
    searchTerm === '' ? 0 : 300
  );

  // ── manage-mode: pre-fetch currently-assigned assets ─────────────────────
  // Strategy A: use useMy with a graph query filtering by profile field.
  // The backend supports this — see the `profile` filter in useQueryAssets /
  // convertFiltersToEntityBlocks and the ?profile= URL helper. We pass a
  // GraphQuery with an equals filter on the `profile` field. A pageSize of 200
  // captures the vast majority of real-world assignments; anything beyond 200
  // will silently be missed (TODO: paginate when bulk-assign endpoint lands).
  const {
    data: assignedAssetsRaw,
    isLoading: isLoadingAssigned,
    isError: isAssignedError,
    hasFetchedData,
    refetch: refetchAssigned,
  } = useMy(
    {
      resource: 'asset',
      query: {
        node: {
          labels: ['Asset' as NodeLabel],
          filters: [
            {
              field: 'profile',
              operator: '=' as Operator,
              value: profile.id,
            },
          ],
        },
        limit: 200,
        page: 0,
      },
    },
    {
      enabled: mode === 'manage' && open,
    }
  );

  // initialKeys is *derived* from the query result, not mirrored into state.
  // Why this matters: useMy returns data:[] when enabled:false — an empty
  // array is truthy in JS. The prior mirror-into-useState pattern fired its
  // init effect on the very first render (modal mounted but closed), latched
  // an `initialKeysLoaded` flag against empty data, and then short-circuited
  // once real data arrived. Deriving with useMemo eliminates the latch
  // entirely so the bug class is structurally impossible. The
  // `isLoadingAssigned` guard is what distinguishes a disabled-query empty
  // array from a settled empty result.
  const initialKeys = useMemo<Set<string>>(() => {
    if (mode !== 'manage' || !hasFetchedData) {
      return new Set();
    }
    return new Set(assignedAssetsRaw.map(a => a.key));
  }, [mode, hasFetchedData, assignedAssetsRaw]);

  // Seed selectedKeys from initialKeys exactly once per open-session. A ref
  // gates re-seeding so background refetches don't clobber user edits, and
  // the open->closed branch resets transient state for the next open.
  const seededRef = useRef(false);
  useEffect(() => {
    if (open) {
      // In create mode, initialKeys is always empty — seed immediately.
      // In manage mode, wait until the pre-fetch has settled (hasFetchedData).
      const canSeed = mode !== 'manage' || hasFetchedData;
      if (!seededRef.current && canSeed) {
        setSelectedKeys(new Set(initialKeys));
        seededRef.current = true;
      }
    } else if (seededRef.current) {
      seededRef.current = false;
      setSelectedKeys(new Set());
      setSearchTerm('');
    }
  }, [open, hasFetchedData, initialKeys, mode]);

  // ── main asset list (all assets, for the picker table) ───────────────────
  const { items, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSearchableList({
      type: 'asset',
      searchTerm: debouncedSearch,
      pageSize: 200,
    });

  // ── cap helpers ─────────────────────────────────────────────────────────
  // In create mode every selected key becomes a PUT, so cap on selectedKeys.size.
  // In manage mode only the diff (pendingAdds + pendingRemoves) fires PUTs, so
  // cap on that delta instead — prevents a false "approaching limit" warning when
  // a profile already has 80 assigned assets and the modal first opens.
  const getPendingChangeCount = (
    selected: Set<string>,
    initial: Set<string>
  ) => {
    if (mode !== 'manage') return selected.size;
    const adds = [...selected].filter(k => !initial.has(k)).length;
    const removes = [...initial].filter(k => !selected.has(k)).length;
    return adds + removes;
  };

  const handleToggle = (item: SearchableListItem) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(item.key)) {
        // Always allow deselect — removing never exceeds the cap
        next.delete(item.key);
      } else {
        // Guard: only add if doing so won't push pending changes over cap.
        // In manage mode: adding a *new* key (not in initialKeys) increments
        // pendingAdds; re-selecting a key that was in initialKeys decrements
        // pendingRemoves — compute prospective count to decide.
        const prospective = new Set(next);
        prospective.add(item.key);
        if (getPendingChangeCount(prospective, initialKeys) > MAX_SELECTION)
          return prev;
        next.add(item.key);
      }
      return next;
    });
  };

  const handleSelectAllVisible = () => {
    setSelectedKeys(prev => {
      const currentPending = getPendingChangeCount(prev, initialKeys);
      const remaining = MAX_SELECTION - currentPending;
      if (remaining <= 0) return prev;
      const next = new Set(prev);
      let added = 0;
      for (const item of items) {
        if (added >= remaining) break;
        if (!next.has(item.key)) {
          next.add(item.key);
          added++;
        }
      }
      return next;
    });
  };

  // Inverse of handleSelectAllVisible: remove only the currently visible
  // (filtered) rows, preserving selections that are scrolled off-screen or
  // filtered out. SelectionTable's header checkbox routes to onClearAll
  // whenever any visible row is selected — a blanket reset would wipe those
  // hidden selections, and in manage mode they are pre-seeded persisted
  // assignments that Save would then bulk-unassign (remove_profile).
  const handleClearAll = () => {
    const visibleKeys = new Set(items.map(item => item.key));
    setSelectedKeys(
      prev => new Set([...prev].filter(k => !visibleKeys.has(k)))
    );
  };

  // ── create-mode apply ────────────────────────────────────────────────────
  const handleApplyCreate = useCallback(async () => {
    if (selectedKeys.size === 0) return;
    setIsApplying(true);
    let hadError = false;

    try {
      const keys = Array.from(selectedKeys);
      const { failures: failedItems } = await runChunked(
        keys,
        key => axios.put('/asset', { key, profile: profile.id }),
        { concurrency: APPLY_CONCURRENCY }
      );
      const failures = failedItems.map(f => f.item);
      hadError = failures.length > 0;

      // TODO: partial failure leaves cache stale for failed assets — acceptable for v1, fix when bulk endpoint lands
      // Optimistically update cached asset rows (mirrors AssetActions.tsx:94-117)
      queryClient.setQueriesData(
        {
          predicate: query => {
            const qk = query.queryKey as string[];
            return qk[0] === 'MY' && qk[1] === 'asset';
          },
        },
        (old: unknown) => {
          if (!old || typeof old !== 'object') return old;
          const data = old as { pages?: { data: Asset[] }[] };
          if (!data.pages) return old;
          return {
            ...data,
            pages: data.pages.map(page => ({
              ...page,
              data: page.data.map((a: Asset) =>
                selectedKeys.has(a.key) ? { ...a, profile: profile.id } : a
              ),
            })),
          };
        }
      );

      // Invalidate so the next fetch (e.g. re-opening Manage Assets or
      // navigating to /assets) reflects the server-confirmed assignments.
      queryClient.invalidateQueries({
        predicate: query => {
          const qk = query.queryKey as string[];
          return qk[0] === 'MY' && qk[1] === 'asset';
        },
      });

      if (hadError) {
        console.warn('[ScanProfile] failed asset keys:', failures);
        toast.error(
          `Failed to assign "${profile.name}" to ${failures.length} of ${keys.length} assets. See browser console for keys.`
        );
      } else {
        toast.success(
          `"${profile.name}" assigned to ${selectedKeys.size} asset${selectedKeys.size === 1 ? '' : 's'}`
        );
        onClose();
      }
    } finally {
      setIsApplying(false);
    }
  }, [selectedKeys, axios, profile, queryClient, onClose]);

  // ── manage-mode save ─────────────────────────────────────────────────────
  const handleSaveManage = useCallback(async () => {
    const pendingAdds = new Set(
      [...selectedKeys].filter(k => !initialKeys.has(k))
    );
    const pendingRemoves = new Set(
      [...initialKeys].filter(k => !selectedKeys.has(k))
    );
    const totalChanges = pendingAdds.size + pendingRemoves.size;
    if (totalChanges === 0) return;

    setIsApplying(true);
    let hadAddError = false;

    try {
      // ── REMOVE half: always go through the bulk endpoint ─────────────────
      // Direct PUT /asset { profile: '' } is silently broken — BaseAsset.MergeFields
      // skips empty-string Profile assignments. The bulk worker uses aws.Graph.Insert
      // which bypasses the merge. params.profile_ids gates the clear so a racing
      // reassignment to a different profile is not clobbered.
      if (pendingRemoves.size > 0) {
        // Submit the bulk job FIRST, then update the cache only on enqueue
        // success. The previous order (cache before mutate) left the UI lying
        // about removals when /bulk/asset returned a 5xx — useActiveBulkJobs
        // can't self-correct because no job exists, so the false state would
        // persist until the next external refetch. Mirrors the create-mode
        // pattern further down (post-mutation update).
        try {
          await submitRemoveProfileBulk.mutateAsync({
            action: 'remove_profile',
            params: { profile_ids: [profile.id] },
            selection: { mode: 'explicit', keys: [...pendingRemoves] },
          });
        } catch {
          // useBulkAction.onError already surfaced a toast. Leave the modal
          // open with original state intact so the user can retry; finally
          // below clears isApplying. Do NOT call onClose — that would force
          // the user to re-open and re-select to retry.
          return;
        }

        // Optimistic cache update — bulk job is accepted; ECS worker will
        // clear the profile asynchronously. useActiveBulkJobs reconciles on
        // job terminal state.
        queryClient.setQueriesData(
          {
            predicate: query => {
              const qk = query.queryKey as string[];
              return qk[0] === 'MY' && qk[1] === 'asset';
            },
          },
          (old: unknown) => {
            if (!old || typeof old !== 'object') return old;
            const data = old as { pages?: { data: Asset[] }[] };
            if (!data.pages) return old;
            return {
              ...data,
              pages: data.pages.map(page => ({
                ...page,
                data: page.data.map((a: Asset) =>
                  pendingRemoves.has(a.key) ? { ...a, profile: undefined } : a
                ),
              })),
            };
          }
        );
        // useBulkAction.onSuccess fires a "Remove profile submitted" info toast
        // and registers the job in bulkJobs.store. No additional toast here.
      }

      // ── ADD half: keep per-item PUTs ─────────────────────────────────────
      // BaseAsset.MergeFields accepts non-empty Profile strings, so PUT /asset
      // { profile: id } works correctly.
      if (pendingAdds.size > 0) {
        const addKeys = [...pendingAdds];
        const { failures: failedItems } = await runChunked(
          addKeys,
          key => axios.put('/asset', { key, profile: profile.id }),
          { concurrency: APPLY_CONCURRENCY }
        );
        const failures = failedItems.map(f => f.item);
        hadAddError = failures.length > 0;

        if (hadAddError) {
          console.warn('[ScanProfile] failed add keys:', failures);
          toast.error(
            `Failed to assign "${profile.name}" to ${failures.length} of ${addKeys.length} assets. See browser console for keys.`
          );
        } else {
          // Optimistic cache update for adds
          queryClient.setQueriesData(
            {
              predicate: query => {
                const qk = query.queryKey as string[];
                return qk[0] === 'MY' && qk[1] === 'asset';
              },
            },
            (old: unknown) => {
              if (!old || typeof old !== 'object') return old;
              const data = old as { pages?: { data: Asset[] }[] };
              if (!data.pages) return old;
              return {
                ...data,
                pages: data.pages.map(page => ({
                  ...page,
                  data: page.data.map((a: Asset) =>
                    pendingAdds.has(a.key) ? { ...a, profile: profile.id } : a
                  ),
                })),
              };
            }
          );

          // Invalidate so the next fetch reflects the server-confirmed state.
          queryClient.invalidateQueries({
            predicate: query => {
              const qk = query.queryKey as string[];
              return qk[0] === 'MY' && qk[1] === 'asset';
            },
          });

          if (pendingRemoves.size === 0) {
            // Only adds happened — emit a summary success toast.
            toast.success(
              `Updated assets for "${profile.name}" (${pendingAdds.size} added)`
            );
          } else {
            // Mixed adds + removes — the bulk submit toast already fired for
            // removes; emit a combined summary.
            toast.success(
              `Updated assets for "${profile.name}" (${pendingAdds.size} added, ${pendingRemoves.size} removed)`
            );
          }
        }
      } else if (pendingRemoves.size > 0 && !hadAddError) {
        // Removes only — the useBulkAction submit toast is the only feedback.
        // No additional success toast to avoid double-toasting.
      }

      if (!hadAddError) {
        onClose();
      }
    } finally {
      setIsApplying(false);
    }
  }, [
    selectedKeys,
    initialKeys,
    axios,
    profile,
    queryClient,
    onClose,
    submitRemoveProfileBulk,
  ]);

  const handleSkip = useCallback(() => {
    toast.success(
      `"${profile.name}" created — assign later from Manage → Set scan profile.`
    );
    onSkip?.();
  }, [profile, onSkip]);

  // Refs deliberately replace `handleSaveManage`/`handleApplyCreate`/
  // `handleSkip`/`onBack`, AND `onFooterStateChange` in the report-up effect
  // deps. Putting any of these in the dep array directly causes a "Maximum
  // update depth exceeded" loop:
  // - `handleSaveManage`/`handleApplyCreate`/`handleSkip` change identity
  //   because their useCallback deps include TanStack-Query-derived arrays
  //   (initialKeys, selectedKeys) that change reference each render.
  // - `onFooterStateChange` is often passed as an inline arrow by consumers
  //   (e.g. `(state) => { capture(state); setFooter(state); }`), which
  //   recreates each render. Reading via ref breaks both cycles.
  // See test 'report-up effect does not loop' for regression coverage.
  const handleSaveManageRef = useRef(handleSaveManage);
  handleSaveManageRef.current = handleSaveManage;
  const handleApplyCreateRef = useRef(handleApplyCreate);
  handleApplyCreateRef.current = handleApplyCreate;
  const handleSkipRef = useRef(handleSkip);
  handleSkipRef.current = handleSkip;
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const onFooterStateChangeRef = useRef(onFooterStateChange);
  onFooterStateChangeRef.current = onFooterStateChange;

  // ── derived state ────────────────────────────────────────────────────────
  const selectedCount = selectedKeys.size;
  const pendingChangeCount = getPendingChangeCount(selectedKeys, initialKeys);
  const showHint = selectedCount === 0 && !searchTerm;

  // manage mode diff
  const totalChanges =
    mode === 'manage'
      ? [...selectedKeys].filter(k => !initialKeys.has(k)).length +
        [...initialKeys].filter(k => !selectedKeys.has(k)).length
      : 0;

  const isManageLoading = mode === 'manage' && isLoadingAssigned;
  const saveLabel =
    mode === 'manage'
      ? 'Save'
      : isApplying
        ? 'Assigning...'
        : `Assign to ${selectedCount} asset${selectedCount === 1 ? '' : 's'}`;

  const isSaveDisabled =
    mode === 'manage'
      ? totalChanges === 0 || isApplying || isAssignedError
      : selectedCount === 0 || isApplying;

  // ── report footer state up ───────────────────────────────────────────────
  useEffect(() => {
    const skipButton =
      mode === 'create' && !onBack ? (
        <Button
          variant="ghost"
          onClick={() => handleSkipRef.current()}
          disabled={isApplying}
        >
          Skip — assign later
        </Button>
      ) : undefined;

    onFooterStateChangeRef.current?.({
      primary: {
        label: isApplying && mode === 'manage' ? 'Saving...' : saveLabel,
        onClick: () =>
          mode === 'manage'
            ? handleSaveManageRef.current()
            : handleApplyCreateRef.current(),
        disabled: isSaveDisabled,
      },
      back: onBack
        ? { onClick: () => onBackRef.current?.(), disabled: isApplying }
        : undefined,
      cancelDisabled: isApplying,
      left: skipButton,
    });
  }, [mode, isApplying, saveLabel, isSaveDisabled, onBack]);

  return (
    <div className="flex flex-col gap-4">
      {/* Step subtitle — shown in wizard context (when onBack is provided) */}
      {Boolean(onBack) && (
        <p className="text-chariot-text-secondary text-sm">
          {mode === 'create'
            ? 'Step 2 of 2 · Choose which assets should use this override. You can change this later from the asset list.'
            : 'Step 2 of 2 · Manage which assets use this override.'}
        </p>
      )}

      {/* Loading state for manage mode pre-fetch */}
      {isManageLoading ? (
        <p className="text-chariot-text-secondary text-sm">
          Loading assigned assets…
        </p>
      ) : isAssignedError && mode === 'manage' ? (
        <div
          data-testid="assign-profile-error-state"
          className="flex flex-col items-center justify-center gap-3 py-8 text-center"
        >
          <p className="text-chariot-status-critical text-sm">
            Couldn&apos;t load currently-assigned assets. Saving now could leave
            the profile in an inconsistent state. Retry to continue.
          </p>
          <Button variant="outline" onClick={() => void refetchAssigned()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          {/* Search */}
          <WizardSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search assets..."
            ariaLabel="Search assets to assign"
            autoFocus
          />

          {/* Selection cap warning */}
          {pendingChangeCount >= 80 && (
            <p aria-live="assertive" className="text-xs">
              {pendingChangeCount >= MAX_SELECTION ? (
                <span className="text-chariot-status-critical">
                  Maximum {MAX_SELECTION} assets per assignment. Deselect to add
                  more.
                </span>
              ) : (
                <span className="text-chariot-status-warning">
                  Approaching limit ({pendingChangeCount}/{MAX_SELECTION}). For
                  larger batches, contact support.
                </span>
              )}
            </p>
          )}

          {/* Table */}
          <SelectionTable
            mode="multi"
            data={items}
            columns={assetColumns}
            selectedKeys={selectedKeys}
            onToggle={handleToggle}
            onSelectAllVisible={handleSelectAllVisible}
            onClearAll={handleClearAll}
            isLoading={isLoading}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            defaultSort={assetDefaultSort}
            maxRows={10}
          />

          {/* Meta row */}
          <p aria-live="polite" className="text-chariot-text-secondary text-sm">
            {showHint ? (
              <span className="text-chariot-text-tertiary text-xs">
                {mode === 'create'
                  ? 'Tip — selecting nothing is fine; you can assign later.'
                  : 'No assets currently assigned. Select assets to assign this profile.'}
              </span>
            ) : (
              <>
                {selectedCount} selected · {items.length} total visible
                {hasNextPage && ' · + more'}
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
}

export function AssignProfileToAssetsModal({
  open,
  onClose,
  onSkip,
  profile,
  mode = 'create',
}: AssignProfileToAssetsModalProps) {
  const [footerState, setFooterState] = useState<ScanProfileFooterState | null>(
    null
  );

  const modalTitle =
    mode === 'manage'
      ? `Manage Assets for "${profile.name}"`
      : `Assign "${profile.name}" to assets`;

  return (
    <Modal
      title={modalTitle}
      open={open}
      onClose={onClose}
      size="4xl"
      footer={
        footerState
          ? {
              text: footerState.primary.label,
              onClick: footerState.primary.onClick,
              disabled: footerState.primary.disabled,
              'data-testid': footerState.primary['data-testid'],
              cancelDisabled: footerState.cancelDisabled,
              left: footerState.left,
            }
          : undefined
      }
    >
      <AssignProfileToAssetsContent
        open={open}
        onClose={onClose}
        onSkip={onSkip}
        profile={profile}
        mode={mode}
        onFooterStateChange={setFooterState}
      />
    </Modal>
  );
}
